/**
 * Ticket LM1: Liste der Freebies.
 *
 * Zugriff über den angemeldeten Client; die Datenbankrichtlinien lassen nur
 * Administratorinnen und Editorinnen lesen und pflegen, Löschen nur Administratorinnen.
 */

import { useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Plus, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminCard } from "@/components/admin/AdminCard";
import { AdminEmptyState } from "@/components/admin/AdminEmptyState";
import { AdminErrorNotice } from "@/components/admin/AdminErrorNotice";
import { AdminSkeletonList } from "@/components/admin/AdminSkeletonList";
import {
  LEAD_MAGNET_KIND_LABELS,
  LEAD_MAGNET_STATUS_LABELS,
  leadMagnetPath,
  type LeadMagnetKind,
  type LeadMagnetStatus,
} from "@/lib/lead-magnets";

export const Route = createFileRoute("/_admin/admin/lead-magnets/")({
  head: () => ({
    meta: [
      { title: "Lead-Magnete – Admin – creategy" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: LeadMagnetsAdminPage,
});

type Row = {
  id: string;
  slug: string;
  thanks_slug: string;
  title: string;
  kind: LeadMagnetKind;
  status: LeadMagnetStatus;
  created_at: string;
};

type SignupRow = { lead_magnet_id: string; confirmed_at: string | null };

function LeadMagnetsAdminPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");

  const magnets = useQuery({
    queryKey: ["admin-lead-magnets"],
    queryFn: async (): Promise<Row[]> => {
      const { data, error } = await supabase
        .from("lead_magnets")
        .select("id, slug, thanks_slug, title, kind, status, created_at")
        .order("created_at", { ascending: false });
      if (error) throw new Error(error.message);
      return (data ?? []) as Row[];
    },
  });

  const signups = useQuery({
    queryKey: ["admin-lead-magnet-signups-counts"],
    queryFn: async (): Promise<SignupRow[]> => {
      const { data, error } = await supabase
        .from("lead_magnet_signups")
        .select("lead_magnet_id, confirmed_at");
      if (error) throw new Error(error.message);
      return (data ?? []) as SignupRow[];
    },
  });

  const counts = useMemo(() => {
    const map = new Map<string, { total: number; confirmed: number }>();
    for (const row of signups.data ?? []) {
      const entry = map.get(row.lead_magnet_id) ?? { total: 0, confirmed: 0 };
      entry.total += 1;
      if (row.confirmed_at) entry.confirmed += 1;
      map.set(row.lead_magnet_id, entry);
    }
    return map;
  }, [signups.data]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const rows = magnets.data ?? [];
    if (!needle) return rows;
    return rows.filter(
      (r) => r.title.toLowerCase().includes(needle) || r.slug.includes(needle),
    );
  }, [magnets.data, search]);

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Lead-Magnete"
        description="Kostenlose Unterlagen mit doppelter Bestätigung. Jede Landingpage liegt unter /kostenlos/ und trägt eine eigene Dankeseite."
        actions={
          <button
            type="button"
            onClick={() => navigate({ to: "/admin/lead-magnets/$id", params: { id: "neu" } })}
            className="inline-flex items-center gap-2 rounded-sm bg-brand-button px-4 py-2 font-display text-sm font-extrabold text-brand-button-foreground"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            Neuer Lead-Magnet
          </button>
        }
      />

      <AdminCard>
        <label className="block text-sm font-medium" htmlFor="lm-search">
          Suche
        </label>
        <input
          id="lm-search"
          className="mt-2 w-full max-w-md rounded-sm border border-border bg-background px-3 py-2 text-sm"
          placeholder="Titel oder Kürzel"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </AdminCard>

      {magnets.error && <AdminErrorNotice message={(magnets.error as Error).message} />}
      {magnets.isLoading && <AdminSkeletonList rows={4} />}

      {!magnets.isLoading && filtered.length === 0 && (
        <AdminEmptyState
          title="Noch kein Lead-Magnet angelegt"
          description="Legen Sie den ersten Freebie an. Bis zur Veröffentlichung ist die Adresse nicht erreichbar."
        />
      )}

      {filtered.length > 0 && (
        <div className="space-y-3">
          {filtered.map((row) => {
            const count = counts.get(row.id) ?? { total: 0, confirmed: 0 };
            return (
              <AdminCard key={row.id}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <Link
                        to="/admin/lead-magnets/$id"
                        params={{ id: row.id }}
                        className="font-display text-base font-extrabold text-brand-petrol hover:underline"
                      >
                        {row.title}
                      </Link>
                      <span
                        className={`rounded-sm px-2 py-0.5 text-xs ${
                          row.status === "veroeffentlicht"
                            ? "bg-brand-button/30 text-foreground"
                            : "bg-muted text-muted-foreground"
                        }`}
                      >
                        {LEAD_MAGNET_STATUS_LABELS[row.status]}
                      </span>
                      <span className="rounded-sm bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                        {LEAD_MAGNET_KIND_LABELS[row.kind]}
                      </span>
                    </div>
                    <p className="mt-2 text-sm text-muted-foreground">
                      {leadMagnetPath(row.slug)} · Dankeseite /{row.thanks_slug}
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {count.confirmed} bestätigt von {count.total} Anmeldungen
                    </p>
                  </div>
                  {row.status === "veroeffentlicht" && (
                    <a
                      href={leadMagnetPath(row.slug)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 rounded-sm border border-border px-3 py-2 text-sm"
                    >
                      <ExternalLink className="h-4 w-4" aria-hidden="true" />
                      Seite ansehen
                    </a>
                  )}
                </div>
              </AdminCard>
            );
          })}
        </div>
      )}
    </div>
  );
}
